/**
 * Whether the chosen pickup date can actually be quoted.
 *
 * Carriers don't pick up at weekends or on public holidays, and holidays are
 * regional — the date is checked against the pickup location's province or
 * state, not ours. Also owns the "change the date" affordance the warnings
 * offer: it scrolls the picker into view and moves the date to the next
 * business day for the pickup country.
 */

import * as MomentHolidays from "@freightsimple/shared/holidays/moment/momentHolidays";
import moment from "moment";
import { useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import {
  nextBusinessDay,
  isWeekend,
  nextBusinessDayForCountry,
} from "@/Shared/Dates/nextBusinessDay";
import type { BookShipmentShipmentType } from "@/Shared/Types/bookShipmentSliceTypes";
import { setPickupDate } from "@/Slices/bookShipmentSlice";

export function useBookShipmentPickupDateStatus(
  shipment: BookShipmentShipmentType,
) {
  const dispatch = useDispatch();
  const { pickupDate, pickupLocation } = shipment;
  const [isPickupDateHoliday, setIsPickupDateHoliday] = useState(false);
  const [isPickupDateWeekend, setIsPickupDateWeekend] = useState(false);
  const datePickerRef = useRef<HTMLDivElement>(null);

  /*
   * Determine if the pickup date is a holiday based on the pickup location
   *
   * If it's a holiday the user should not get quotes
   */
  useEffect(() => {
    const pickupState = pickupLocation.address.stateOrProvinceCode;

    if (pickupState && pickupDate) {
      const response = MomentHolidays.isHolidayForState(
        moment(pickupDate),
        pickupState,
      );
      setIsPickupDateHoliday(response);
    }
  }, [pickupLocation, pickupDate]);

  useEffect(() => {
    setIsPickupDateWeekend(isWeekend(moment(pickupDate)));
  }, [pickupDate]);

  function scrollToPickupDate() {
    if (datePickerRef && datePickerRef.current) {
      datePickerRef.current.scrollIntoView();

      const pickupCountry = pickupLocation.address.countryCode;

      let newDate = nextBusinessDay();

      if (pickupCountry) {
        newDate = nextBusinessDayForCountry(pickupCountry);
      }

      dispatch(setPickupDate(newDate.format("YYYY-MM-DD")));
    }
  }

  return {
    isPickupDateHoliday,
    isPickupDateWeekend,
    datePickerRef,
    scrollToPickupDate,
  };
}
